import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Loader2 } from 'lucide-react';
import { acceptHomeInvite, type HomeInviteAcceptResult } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import styles from './MyHomePage.module.css';

/**
 * /home-invite?token=… — the shareable home invite link's landing page
 * (Residential household slice). Public route: works signed in or out,
 * like /join and /join-fleet.
 *
 *   Signed in  — accepts immediately: free pass list + household viewer
 *                + auto-friend with the owner, then shows who invited
 *                them and routes onward.
 *   Signed out — stashes the token in sessionStorage (the same vehicle
 *                the referral ?ref uses to survive the auth trip,
 *                including the Google OAuth redirect) and offers
 *                create-account / sign-in. App.tsx's post-auth effect
 *                finds the stash and accepts — so register, login, and
 *                OAuth all converge without this page needing to be
 *                revisited.
 */
export const HOME_INVITE_STORAGE_KEY = 'evi_home_invite';

interface Props { onNavigate: (path: string) => void }

type ViewState =
  | { kind: 'accepting' }
  | { kind: 'accepted'; result: HomeInviteAcceptResult }
  | { kind: 'invalid' }
  | { kind: 'signed_out' };

export function HomeInvitePage({ onNavigate }: Props) {
  const { t } = useTranslation('homeInvite');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [view, setView] = useState<ViewState>(() => {
    if (!token) return { kind: 'invalid' };
    return isAuthenticated ? { kind: 'accepting' } : { kind: 'signed_out' };
  });

  useEffect(() => {
    if (!token) return;
    if (!isAuthenticated) {
      // Survive the auth trip; App.tsx accepts once the session exists.
      sessionStorage.setItem(HOME_INVITE_STORAGE_KEY, token);
      return;
    }
    let cancelled = false;
    acceptHomeInvite(token)
      .then((result) => {
        sessionStorage.removeItem(HOME_INVITE_STORAGE_KEY);
        if (!cancelled) setView({ kind: 'accepted', result });
      })
      .catch(() => {
        if (!cancelled) setView({ kind: 'invalid' });
      });
    return () => { cancelled = true; };
    // Token and auth state are stable for the life of this page view.
  }, [isAuthenticated, token]);

  return (
    <div className={styles.page}>
      <div className={styles.empty}>
        <div className={styles.emptyIcon}><Home size={26} /></div>

        {view.kind === 'accepting' && (
          <>
            <h1 className={styles.emptyTitle}>{t('accepting.title')}</h1>
            <p className={styles.emptyDesc}>
              <Loader2 size={14} className={styles.spin} /> {t('accepting.desc')}
            </p>
          </>
        )}

        {view.kind === 'accepted' && (
          <>
            <h1 className={styles.emptyTitle}>
              {view.result.already ? t('accepted.titleAlready') : t('accepted.titleWelcome')}
            </h1>
            <p className={styles.emptyDesc}>
              {view.result.already
                ? t('accepted.descAlready', { homeName: view.result.home_name })
                : t('accepted.descWelcome', {
                    ownerName: view.result.owner_name,
                    homeName: view.result.home_name,
                  })}
            </p>
            <button
              type="button"
              className={styles.emptyCta}
              onClick={() => onNavigate('/my-home')}
            >
              {t('accepted.cta')}
            </button>
          </>
        )}

        {view.kind === 'invalid' && (
          <>
            <h1 className={styles.emptyTitle}>{t('invalid.title')}</h1>
            <p className={styles.emptyDesc}>
              {t('invalid.desc')}
            </p>
            <button
              type="button"
              className={styles.emptyCta}
              onClick={() => onNavigate('/map')}
            >
              {t('invalid.cta')}
            </button>
          </>
        )}

        {view.kind === 'signed_out' && (
          <>
            <h1 className={styles.emptyTitle}>{t('signedOut.title')}</h1>
            <p className={styles.emptyDesc}>
              {t('signedOut.desc')}
            </p>
            <button
              type="button"
              className={styles.emptyCta}
              onClick={() => onNavigate('/register')}
            >
              {t('signedOut.createAccount')}
            </button>
            <button
              type="button"
              className={styles.settingsRow}
              onClick={() => onNavigate('/login')}
            >
              {t('signedOut.haveAccount')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
